import * as React from 'react';

import { dateStringToUTCStart } from '@rematter/common/dateTime';
import {
  CurrencyCode,
  enumQuoteType,
  FreightTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import { Button, ConsoleDialog, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { quoteCreateAtom } from '../../_graphql/mutationQuoteCreate';
import { quoteGetNextDocumentNumberAtom } from '../../_graphql/mutationQuoteGetNextDocumentNumber';
import {
  $canSubmitQuoteForm,
  $isQuoteFormSubmitting,
  $quoteForm,
  $resetQuoteFormForCreate,
} from '../../_state/quoteForm.state';
import * as state from '../../_state/quotesList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { QuoteInfoForm } from './QuoteInfoForm';

interface QuoteCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ QUOTE CREATE DIALOG ⚛️ ⚛️ ⚛️
//
export const QuoteCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: QuoteCreateDialogProps) => {
  const t = useTranslations();

  const [documentNumber, setDocumentNumber] = React.useState('');
  const [isLoadingDocNumber, setIsLoadingDocNumber] = React.useState(false);

  const canSubmit = useAtomValue($canSubmitQuoteForm);
  const isSubmitting = useAtomValue($isQuoteFormSubmitting);
  const form = useAtomValue($quoteForm);
  const { refetch } = useAtomValue(state.$remoteQuotes);

  const setIsSubmitting = useSetAtom($isQuoteFormSubmitting);
  const resetForm = useSetAtom($resetQuoteFormForCreate);
  const createQuote = useSetAtom(quoteCreateAtom({}));
  const nextDocumentNumber = useSetAtom(quoteGetNextDocumentNumberAtom({}));

  const toast = useToast();

  const handleClose = React.useCallback(() => {
    resetForm();
    setDocumentNumber('');
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!form.accountId) {
      toast.error(t.quotes.validation.selectAccount);
      return;
    }

    try {
      setIsSubmitting(true);
      await createQuote({
        documentNumber,
        type:
          form.type === 'Purchase'
            ? enumQuoteType.Purchase
            : enumQuoteType.Sales,
        accountId: form.accountId,
        facilityId: form.facilityId ?? undefined,
        validUntil: dateStringToUTCStart(form.validUntil),
        expectedShipDate: dateStringToUTCStart(form.expectedShipDate),
        paymentTerm: (form.paymentTerm as PaymentTerm) || undefined,
        paymentCurrency: (form.paymentCurrency as CurrencyCode) || undefined,
        freightTerm: (form.freightTerm as FreightTerm) || undefined,
        freightCoveredByCustomer: form.freightCoveredByCustomer ?? undefined,
        notes: form.notes || undefined,
      });
      toast.success(t.quotes.toast.created);
      refetch?.();
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.quotes.toast.createError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    documentNumber,
    createQuote,
    setIsSubmitting,
    toast,
    refetch,
    onSuccess,
    handleClose,
    t,
  ]);

  React.useEffect(() => {
    if (!isOpen) return;
    resetForm();
    setDocumentNumber('');
  }, [isOpen, resetForm]);

  const needsDocumentNumber = isOpen && !documentNumber;

  React.useEffect(() => {
    if (!needsDocumentNumber) return;

    const abortController = new AbortController();
    setIsLoadingDocNumber(true);

    nextDocumentNumber({})
      .then((result) => {
        if (abortController.signal.aborted) return;
        setDocumentNumber(result.tradeQuoteNextDocumentNumber);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        toast.error(error instanceof Error ? error.message : 'Unknown error');
        onClose();
      })
      .finally(() => {
        if (abortController.signal.aborted) return;
        setIsLoadingDocNumber(false);
      });

    return () => {
      abortController.abort();
    };
  }, [needsDocumentNumber, nextDocumentNumber, toast, onClose]);

  return (
    <ConsoleDialog
      className="@QuoteCreateDialog"
      contentClassName="p-0 pb-5"
      isOpen={isOpen}
      isLoading={isLoadingDocNumber}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.quotes.create.titleWithType(
        form.type === enumQuoteType.Purchase
          ? t.quotes.types.purchase
          : t.quotes.types.sales,
      )}
      content={
        <>
          <div className="px-5 pt-5">
            <TextField
              label={t.quotes.detail.fields.quoteNumber}
              value={documentNumber}
              onChange={setDocumentNumber}
              placeholder={t.common.placeholders.generateNumber}
              isRequired
              isDisabled={isSubmitting}
            />
          </div>
          <QuoteInfoForm mode="create" disabled={isSubmitting} />
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || !documentNumber}
          >
            {isSubmitting
              ? t.common.loading.creating
              : t.quotes.create.submitButton}
          </Button>
        </div>
      }
    />
  );
};
